import { ListItem } from '@lmc/ui-kit'
import { Tag, ListItemProps } from '@lmc/ui-kit'
import { Carer, GoalStatus } from '@lmc/ts-common'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import styled from 'styled-components'
import DotsMenuSupport from '../SupportPlanInput/DotsMenuSupport'

const GoalItem = (props: GoalItemProps) => {
  const {
    id,
    level,
    status,
    editedBy,
    editedOn,
    createdBy,
    createdOn,
    disabled,
    onUnlink,
    testId,
    ...restProps
  } = props

  const levelTagColors = getTagColorByLevel(level)
  const statusTagColor = getTagColorByStatus(status)

  const { t } = useTranslation(NAMESPACE.GOALS)

  const formatedDateTime = getFormatedDateTime(editedOn, createdOn)
  const lastReviewerId =
    (editedBy && editedBy !== 'undefined' && editedBy) ||
    (createdBy && createdBy !== 'undefined' && createdBy)

  const carers: Carer[] = useSelector((state: any) => state.data.carers)
  const lastReviewer = carers?.find((carer) => carer.id === lastReviewerId)
  const lastReviewerName = `${lastReviewer?.name.first} ${lastReviewer?.name.last}`

  const lastReviewedString = `${t(
    goalsConstants.LAST_REVIEWED_ON
  )} ${formatedDateTime} - ${lastReviewerName}`

  const baseId = testId || 'goal-item'

  return (
    <ListItem
      {...restProps}
      style={styles.item}
      subtitleStyle={styles.subtitle}
      subTitleContent={lastReviewedString}
      hasBorder
      data-testid={baseId}
    >
      <TagsWrapper data-testid={`${baseId}-tags`}>
        <Tag
          style={{ color: levelTagColors.textColor }}
          color={levelTagColors.color}
          data-testid={`${baseId}-level-tag`}
        >
          {level}
        </Tag>
        {disabled ? (
          <TagUpperCase
            color={statusTagColor}
            data-testid={`${baseId}-status-tag`}
          >
            {t(goalsConstants[status])}
          </TagUpperCase>
        ) : (
          <DotsMenuSupport onUnlink={() => onUnlink?.(id)} />
        )}
      </TagsWrapper>
    </ListItem>
  )
}

const getFormatedDateTime = (
  editedOn: string | undefined,
  createdOn: string
) => {
  const lastReviewdDateString = editedOn || createdOn
  const lastReviewdDate = dayjs(lastReviewdDateString).format('DD-MM-YYYY')
  const lastReviewdTime = new Date(lastReviewdDateString).toLocaleTimeString()

  const formatedDateTime = `${lastReviewdDate} - ${lastReviewdTime}`

  return formatedDateTime
}

export const getTagColorByLevel = (
  level: number | undefined
): { color: string | undefined; textColor?: string } => {
  if (!level || level < 4) {
    return {
      color: 'default'
    }
  }

  if (level < 7) {
    return {
      color: 'green'
    }
  }

  if (level < 10) {
    return {
      color: 'orange'
    }
  }

  return {
    color: 'magenta'
  }
}

export const getTagColorByStatus = (status: GoalStatus | undefined) => {
  if (status === ('done' as GoalStatus)) {
    return 'green'
  }

  if (status === ('inProgress' as GoalStatus)) {
    return 'blue'
  }

  return undefined
}

export interface GoalItemBase extends ListItemProps {
  level: number
  status: GoalStatus
  editedOn?: string
  editedBy?: string
  createdBy: string
  createdOn: string
  disabled: boolean
  hasBorder?: boolean
  title: string
  progress?: number
  nextReviewDate?: string
}

interface GoalItemProps extends GoalItemBase {
  id: string
  /**
   * if disabled = true, onUnlink can be omitted
   */
  onUnlink?: (id: string) => void | undefined
  hasBorder?: boolean
  testId?: string
}

const styles = {
  item: {
    height: 60,
    paddingRight: 8,
    paddingLeft: 16
  },
  subtitle: { opacity: 0.7 }
}

const TagsWrapper = styled.div`
  display: flex;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }
`

export const TagUpperCase = styled(Tag)`
  text-transform: uppercase;
  display: flex;
  align-items: center;
  padding: 4px 8px;
`

export default GoalItem
